import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../api/client';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'USER';
  company_name?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to decode JWT payload locally without dependencies
const parseJwt = (token: string): UserProfile | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const initAuth = () => {
    // Check URL parameters for QuirkyQode SSO token
    const urlParams = new URLSearchParams(window.location.search);
    const qqToken = urlParams.get('qq_token');
    if (qqToken) {
      localStorage.setItem('accessToken', qqToken);
      localStorage.setItem('qq_token', qqToken);
      // Clean up URL search query
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    const token = localStorage.getItem('accessToken') || localStorage.getItem('qq_token');
    if (token) {
      const decoded = parseJwt(token);
      if (decoded && ((decoded as any).exp ? (decoded as any).exp * 1000 > Date.now() : true)) {
        const nameFallback = decoded.name || decoded.company_name || (decoded.email ? decoded.email.split('@')[0] : 'QuirkyQode User');

        const decodedRole = (decoded as any)?.role;
        const assignedRole: 'ADMIN' | 'USER' = decodedRole === 'client' || decodedRole === 'ADMIN' || decodedRole === 'org_user' ? 'ADMIN' : 'USER';

        setUser({
          id: decoded.id || 'qq_user',
          name: nameFallback,
          email: decoded.email || 'user@quirkyqode.com',
          role: assignedRole
        });
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('qq_token');
        setUser(null);
      }
    } else {
      setUser(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    initAuth();

    // Listen to API client forced logout events
    const handleForcedLogout = () => {
      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    };

    window.addEventListener('auth-logout', handleForcedLogout);
    return () => window.removeEventListener('auth-logout', handleForcedLogout);
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { accessToken, refreshToken, user: loggedUser } = response.data;
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      setUser(loggedUser);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/auth/register', { name, email, password });
      const { accessToken, refreshToken, user: registeredUser } = response.data;
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      setUser(registeredUser);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiClient.post('/auth/logout');
    } catch (e) {
      console.warn('API logout failed, performing local logout');
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('qq_token');
      setUser(null);
      setIsLoading(false);
      window.location.href = process.env.REACT_APP_QQ_PORTAL_URL || 'http://localhost:7001/my-tools';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
